/**
 * Centralized auth state purge
 *
 * Wipes every known piece of auth state the app owns: provider cache,
 * Kinde/MSAL storage keys, legacy session tokens, RTK Query cache,
 * the 401 redirect flag, and the redux-persist store.
 *
 * Idempotent. Safe to call multiple times or without an active session.
 * Each cleanup step is independent — a failure in one does not skip the rest.
 *
 * Uses dynamic imports for store/api/baseQueryWithReauth to avoid circular
 * dependencies (those modules transitively import each other).
 *
 * @module purgeAuthState
 */

import { clearAuthProvider } from '@/common/helpers/auth/authProviderDetection'

const KINDE_KEY_PATTERNS = ['kinde_', 'kinde-']
const MSAL_KEY_PATTERNS = ['msal.', 'login.windows.net', 'b2clogin.com', 'login.microsoftonline.com']
const LEGACY_SESSION_KEYS = ['accessToken', 'accessTokenExpiresOn']

/**
 * Scans a Storage object and removes all keys matching any of the given patterns.
 *
 * @param {Storage} storage - localStorage or sessionStorage
 * @param {string[]} patterns - substrings to match against key names
 */
const scanAndRemove = (storage, patterns) => {
    const toRemove = []
    for (let i = 0; i < storage.length; i++) {
        const key = storage.key(i)
        if (key && patterns.some((p) => key.includes(p))) toRemove.push(key)
    }
    toRemove.forEach((k) => storage.removeItem(k))
}

/**
 * Runs a cleanup step, logging a warning on failure without throwing.
 *
 * @param {string} label - Description for the warning message
 * @param {Function} fn - Cleanup function (sync or async)
 */
const safeRun = async (label, fn) => {
    try {
        await fn()
    } catch (error) {
        console.warn(`[purgeAuthState] ${label} failed — continuing`, error)
    }
}

/**
 * Wipes every known piece of auth state the app owns.
 * Idempotent. Safe to call multiple times; safe to call without an active session.
 *
 * @returns {Promise<void>}
 */
export const purgeAuthState = async () => {
    await safeRun('clearAuthProvider', () => clearAuthProvider())
    await safeRun('localStorage cleanup', () =>
        scanAndRemove(localStorage, [...KINDE_KEY_PATTERNS, ...MSAL_KEY_PATTERNS])
    )
    await safeRun('sessionStorage cleanup', () => {
        scanAndRemove(sessionStorage, [...KINDE_KEY_PATTERNS, ...MSAL_KEY_PATTERNS])
        LEGACY_SESSION_KEYS.forEach((k) => sessionStorage.removeItem(k))
    })

    // Dynamic imports to avoid circular dependency:
    // purgeAuthState → store → api → baseQueryWithReauth → purgeAuthState
    await safeRun('RTK + persistor reset', async () => {
        const [{ api }, { _resetRedirectingFlag }, { persistor, store }] = await Promise.all([
            import('@/app/services/api'),
            import('@/app/services/baseQueryWithReauth'),
            import('@/app/store'),
        ])
        store.dispatch(api.util.resetApiState())
        _resetRedirectingFlag()
        await persistor.purge()
    })
}
